export const RESERVED_STORE_SUBDOMAINS = new Set([
    'www',
    'api',
    'admin',
    'app',
    'dash',
    'mail',
    'smtp',
    'ftp',
    'cdn',
    'static',
    'assets',
    'platform',
    'dashboard',
    'billing',
    'support',
    'status',
    'stores',
    'elvix',
    'zilvix',
    'rovtrix',
    'sylvixr',
    'shop',
    'marketing',
]);

/** Brand labels used in nested hosts: {sub}.{brand}.quazbit.com */
const BRAND_HOST_LABELS = new Set(['elvix', 'zilvix', 'rovtrix', 'sylvixr']);

/** Hosting platforms — never treat these as a merchant custom domain. */
const PLATFORM_HOST_SUFFIXES = [
    '.workers.dev',
    '.pages.dev',
    '.vercel.app',
    '.netlify.app',
    '.onrender.com',
    '.railway.app',
    '.fly.dev',
    '.github.io',
    '.cloudflare.com',
    '.trycloudflare.com',
];

export function isPlatformHostedHostname(host = '') {
    const hostname = stripPort(host);
    if (!hostname) return false;
    return PLATFORM_HOST_SUFFIXES.some((suffix) => hostname === suffix.slice(1) || hostname.endsWith(suffix));
}

function stripPort(host = '') {
    return String(host).trim().toLowerCase().split(':')[0];
}

export function getPlatformDomain(env = process.env) {
    return String(
        env.REACT_APP_STOREFRONT_PLATFORM_DOMAIN ||
            env.NEXT_PUBLIC_STOREFRONT_PLATFORM_DOMAIN ||
            'localhost',
    )
        .trim()
        .toLowerCase();
}

/**
 * Extract store subdomain from the browser hostname.
 * Examples:
 *   shop.zilvix.quazbit.com → shop (platformDomain = zilvix.quazbit.com)
 *   shop.quazbit.com → shop (platformDomain = quazbit.com)
 *   downtown-cafe.localhost → downtown-cafe
 */
export function parseSubdomainFromHost(host, platformDomain = getPlatformDomain()) {
    const hostname = stripPort(host);
    const domain = String(platformDomain || '').trim().toLowerCase();

    if (!hostname || !domain) return null;

    if (hostname === domain || hostname === `www.${domain}`) {
        return null;
    }

    if (domain === 'localhost' && hostname.endsWith('.localhost')) {
        const sub = hostname.slice(0, -'.localhost'.length);
        if (!sub || sub.includes('.')) return null;
        if (RESERVED_STORE_SUBDOMAINS.has(sub)) return null;
        return sub;
    }

    const suffix = `.${domain}`;
    if (hostname.endsWith(suffix)) {
        const sub = hostname.slice(0, -suffix.length);
        if (sub && !sub.includes('.') && !RESERVED_STORE_SUBDOMAINS.has(sub)) {
            return sub;
        }
        // platformDomain=quazbit.com but host is shop.zilvix.quazbit.com → keep trying fallbacks
    }

    const parts = hostname.split('.').filter(Boolean);

    // Nested brand merchant: shop.zilvix.quazbit.com
    if (parts.length === 4 && parts[2] === 'quazbit' && parts[3] === 'com') {
        const [sub, brand] = parts;
        if (
            BRAND_HOST_LABELS.has(brand) &&
            sub &&
            !RESERVED_STORE_SUBDOMAINS.has(sub)
        ) {
            return sub;
        }
    }

    // Flat merchant when platform domain was baked wrong (e.g. localhost): shop.quazbit.com
    if (parts.length === 3) {
        const sub = parts[0];
        if (sub && !RESERVED_STORE_SUBDOMAINS.has(sub)) return sub;
    }

    return null;
}

export function buildStorefrontSubdomainHost(subdomain, platformDomain = getPlatformDomain()) {
    const sub = String(subdomain || '').trim().toLowerCase();
    const domain = String(platformDomain || '').trim().toLowerCase();
    if (!sub || !domain) return '';
    if (domain === 'localhost') return `${sub}.localhost`;
    return `${sub}.${domain}`;
}

export function parseCustomDomainFromHost(host, platformDomain = getPlatformDomain()) {
    const hostname = String(host || '').trim().toLowerCase().split(':')[0];
    const domain = String(platformDomain || '').trim().toLowerCase();

    if (!hostname) return null;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return null;
    if (hostname.endsWith('.localhost')) return null;
    if (isPlatformHostedHostname(hostname)) return null;
    if (domain && (hostname === domain || hostname === `www.${domain}`)) return null;
    if (domain && hostname.endsWith(`.${domain}`)) return null;

    if (parseSubdomainFromHost(hostname, domain || 'invalid.invalid')) return null;

    return hostname;
}
